import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Star, Building2, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'communication', label: 'Communication' },
  { key: 'punctuality', label: 'Punctuality' },
  { key: 'professionalism', label: 'Professionalism' },
  { key: 'care_quality', label: 'Care Quality' },
];

function StarRating({ value, onChange, size = 'w-6 h-6' }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(s => (
        <button key={s} onClick={() => onChange(s)} type="button"
          className="transition-transform hover:scale-110">
          <Star className={`${size} ${s <= value ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`} />
        </button>
      ))}
    </div>
  );
}

export default function ClientRateAgency() {
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [categories, setCategories] = useState({});
  const [existing, setExisting] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/client-portal/my-agency-rating`, { withCredentials: true })
      .then(res => {
        if (res.data && res.data.rating) {
          setExisting(res.data);
          setRating(res.data.rating);
          setReview(res.data.review || '');
          setCategories(res.data.categories || {});
        }
      }).catch(console.error);
  }, []);

  const handleSubmit = async () => {
    if (rating === 0) { toast.error('Please select a rating'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/client-portal/rate-agency`, {
        rating, review, categories
      }, { withCredentials: true });
      toast.success(existing ? 'Rating updated' : 'Rating submitted');
      setExisting({ rating, review, categories });
    } catch (err) {
      toast.error('Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-rate-agency-page">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Rate Your Agency</h1>
          <p className="text-sm text-muted-foreground mt-1">Share your experience with the care agency</p>
        </div>

        {existing && (
          <Card className="glass-card border-[#A8E6CF]/30">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#A8E6CF]" />
              <p className="text-sm text-foreground">
                You've rated this agency <span className="font-bold text-amber-400">{existing.rating}/5</span>. You can update your rating below.
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="glass-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#00A6A0]" /> Overall Rating
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="text-center">
              <StarRating value={rating} onChange={setRating} size="w-10 h-10" />
              <p className="text-sm text-muted-foreground mt-2">
                {rating === 0 ? 'Tap to rate' : `${rating} out of 5`}
              </p>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-medium text-foreground">Rate by category (optional)</p>
              {CATEGORIES.map(cat => (
                <div key={cat.key} className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{cat.label}</span>
                  <StarRating value={categories[cat.key] || 0}
                    onChange={v => setCategories({...categories, [cat.key]: v})}
                    size="w-5 h-5" />
                </div>
              ))}
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Written Review (optional)</label>
              <Textarea value={review} rows={3}
                onChange={e => setReview(e.target.value)}
                placeholder="Tell us about your experience..."
                className="bg-muted border-border" data-testid="agency-review-text" />
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSubmit} disabled={submitting} className="btn-primary gap-2" data-testid="submit-agency-rating-btn">
                <Send className="w-4 h-4" /> {submitting ? 'Submitting...' : existing ? 'Update Rating' : 'Submit Rating'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
